<template>
  <form @submit.prevent>
    <!-- Greeting -->
    <div class="mb-3">
      <label class="form-label">
        Greeting <span class="text-danger">*</span>
      </label>

      <select
          v-model="form.greeting"
          class="form-select"
          :class="{ 'is-invalid': errors.greeting }"
          required=""
      >
        <option value="">Select greeting</option>
        <option
            v-for="greeting in greetings"
            :key="greeting.value"
            :value="greeting.value"
        >
          {{ greeting.label }}
        </option>
      </select>

      <div v-if="errors.greeting" class="invalid-feedback">
        {{ errors.greeting }}
      </div>
    </div>

    <!-- Campaign Type -->
    <div class="mb-3">
      <label class="form-label">
        Campaign Type <span class="text-danger">*</span>
      </label>

      <select
          v-model="form.type"
          class="form-select"
          :class="{ 'is-invalid': errors.type }"
      >
        <option value="">Select campaign type</option>
        <option
            v-for="type in campaignTypes"
            :key="type.value"
            :value="type.value"
        >
          {{ type.label }}
        </option>
      </select>

      <div v-if="errors.type" class="invalid-feedback">
        {{ errors.type }}
      </div>
    </div>

    <!-- Description -->
    <div class="mb-3">
      <label class="form-label">
        Description <span class="text-danger">*</span>
      </label>


      <textarea
          v-model="form.description"
          :class="{ 'is-invalid': errors.description }"
          class="form-control"
          rows="4"
          placeholder="Enter campaign description"
          maxlength="180"
          @paste.prevent="form.description = $event.clipboardData.getData('text').slice(0, 180)"
      />
      <small class="text-muted">
        {{ form.description ? form.description.length : 0 }}/180
      </small>

      <div v-if="errors.description" class="invalid-feedback">
        {{ errors.description }}
      </div>
    </div>

    <!-- Message -->
    <div class="mb-3">
      <label class="form-label">Message</label>


      <textarea
          v-model="form.message"
          :class="{ 'is-invalid': errors.message }"
          class="form-control"
          rows="4"
          placeholder="Enter campaign message"
          maxlength="50"
          @paste.prevent="form.message = $event.clipboardData.getData('text').slice(0, 50)"
      />
      <small class="text-muted">
        {{ form.message ? form.message.length : 0 }}/50
      </small>
      <div v-if="errors.message" class="invalid-feedback">
        {{ errors.message }}
      </div>
    </div>

    <!-- Start / End -->
    <div class="row">
      <div class="col-md-6 mb-3">
        <label class="form-label">
          Start Date <span class="text-danger">*</span>
        </label>

        <input
            v-model="form.start_date"
            :class="{ 'is-invalid': errors.start_date }"
            type="datetime-local"
            class="form-control"
            :min="minStartDate"
        />
        <div v-if="errors.start_date" class="invalid-feedback">
          {{ errors.start_date }}
        </div>
      </div>

      <div class="col-md-6 mb-3">
        <label class="form-label">
          End Date <span class="text-danger">*</span>
        </label>

        <input
            v-model="form.end_date"
            :class="{ 'is-invalid': errors.end_date }"
            type="datetime-local"
            class="form-control"
            :min="minEndDate"
        />
        <div v-if="errors.end_date" class="invalid-feedback">
          {{ errors.end_date }}
        </div>
      </div>
    </div>

    <!-- Action -->
    <div class="row">
      <div class="col-md-6 mb-3">
        <label class="form-label">
          Action Text
        </label>

        <input
            v-model="form.action_text"
            type="text"
            class="form-control"
            placeholder="e.g. Donate Now"
        />
      </div>

      <div class="col-md-6 mb-3" v-if="form.type !== 'Donation'">
        <label class="form-label">
          Action URL
          <span
              v-if="form.type !== 'Donation'"
              class="text-danger"
          >
            *
          </span>
        </label>

        <input
            v-model="form.action_url"
            type="url"
            class="form-control"
            placeholder="https://example.com"
        />

        <div v-if="errors.action_url" class="text-danger mt-1">
          {{ errors.action_url }}
        </div>
      </div>

      <!-- Donation Amount -->
      <div
          v-if="form.type === 'Donation'"
          class="col-md-6 mb-3"
      >
        <label class="form-label">
          Donation Amount 
        </label>

        <input
            v-model.number="form.amount"
            :class="{ 'is-invalid': errors.amount }"
            type="number"
            min="500"
            class="form-control"
            placeholder="Minimum 500"
        />

        <div v-if="errors.amount" class="text-danger mt-1">
          {{ errors.amount }}
        </div>
      </div>
    </div>


    <!-- Color -->
    <div class="mb-3">
      <label class="form-label">
        Campaign Color <span class="text-danger">*</span>
      </label>

      <div class="d-flex flex-wrap gap-2">

        <div class="color-options">
          <button
              v-for="color in colors"
              :key="color.color_code"
              type="button"
              class="color-option"
              :class="{
            active: form.color_code === color.color_code
        }"
              :style="{
            backgroundColor: color.color_code
        }"
              :title="color.color_name"
              @click="selectColor(color.color_code)"
          />

          <div v-if="errors.color_code" class="text-danger mt-1">
            {{ errors.color_code }}
          </div>
        </div>
      </div>
    </div>

    <!-- Photo -->
    <div class="mb-3">
      <label class="form-label">
        Campaign Photo
      </label>


      <div class="file-upload" @click="openModal(false)">
                <span role="img" aria-label="plus" class="anticon anticon-plus">
                  <svg focusable="false" data-icon="plus"
                       width="1em" height="1em"
                       fill="currentColor"
                       aria-hidden="true"
                       viewBox="64 64 896 896">
                  <path d="M482 152h60q8 0 8 8v704q0 8-8 8h-60q-8 0-8-8V160q0-8 8-8z"></path>
                  <path d="M192 474h672q8 0 8 8v60q0 8-8 8H160q-8 0-8-8v-60q0-8 8-8z"></path>
                </svg><!----></span>
        <div class="file-upload-text">Upload</div>
      </div>
    </div>
  </form>

  <MediaModal
      ref="mediaModal"
      :media="media"
      :select-multiple="selectMultiple"
      @selected="handleMediaSelected"
  />

</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import {api} from "@/configs";
import {apiUrls} from "@/constants";
import MediaModal from "@/views/pages/modals/mediaModal.vue";
import {useMediaStore} from "@/store/mediaStore";
import {validateCampaign} from "@/views/pages/Campaign/utils/campaign-validation";

const mediaStore = useMediaStore();
const selectMultiple = ref(false)

const errors = ref({});

function validate() {
  errors.value = validateCampaign(form.value);

  return Object.keys(errors.value).length === 0;
}

defineExpose({
  validate,
});

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  'update:modelValue',
]);

const form = computed({
  get: () => props.modelValue,

  set: (value) => {
    emit('update:modelValue', value);
  },
});

const media = computed(() => mediaStore.media);

const greetings = [
  {
    value: 'Hello',
    label: 'Hello',
  },
  {
    value: 'Hi',
    label: 'Hi',
  },
  {
    value: 'Hey',
    label: 'Hey',
  },
  {
    value: 'Welcome',
    label: 'Welcome',
  },
  {
    value: 'Dear',
    label: 'Dear',
  },
  {
    value: 'welcome',
    label: 'Welcome',
  },
];

const campaignTypes = [
  {
    value: 'Donation',
    label: 'Donation',
  },
  {
    value: 'Awareness',
    label: 'Awareness',
  },
   {
    value: 'Announcement',
    label: 'Announcement',
  },
  {
    value: 'Sponsorship',
    label: 'Sponsorship',
  },
  {
    value: 'Promotion',
    label: 'Promotion',
  }
];

const colors = ref([]);

async function fetchColors() {
  try {
    const response = await api.get(apiUrls.campaign + '/colors');

    colors.value = response.data.data;
  } catch (error) {
    console.error('Failed to fetch campaign colors', error);
  }
}

async function fetchMedia() {
  await mediaStore.fetchMedia();
}

function selectColor(colorCode) {
  form.value.color_code = colorCode;
}

function handleMediaSelected(selected) {
  if (!selectMultiple.value) {
    form.value.media_item_url = selected[0]?.url || '';
    form.value.media_item_id = selected[0]?.id || '';
  }
}

const minStartDate = computed(() => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
});

const minEndDate = computed(() => {
  return form.value.start_date || minStartDate.value;
});

function openModal(value) {
  selectMultiple.value = value;
  $("#mediaModal").modal("show");
}

// Fixed: Using Composition API watch imported from 'vue'
watch(() => form.value?.description, (value) => {
  if (value && value.length > 180) {
    form.value.description = value.slice(0, 180);
  }
});

watch(() => form.value?.message, (value) => {
  if (value && value.length > 50) {
    form.value.message = value.slice(0, 50);
  }
});

onMounted(() => {
  fetchMedia();
  fetchColors();
});
</script>

<style scoped>
.color-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 100%;
}

.color-option {
  width: 35px;
  height: 35px;
  min-width: 35px;
  flex: 0 0 35px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  cursor: pointer;
}

.color-option.active {
  border-color: #000;
  box-shadow: 0 0 0 2px #fff;
}
</style>